/**
 * EVERY BLOCK'S PROBLEMS, FROM OUTSIDE THE BLOCK.
 *
 * Until this file, a block's checks could only be run by the block itself: fourteen of the sixteen
 * issue functions were module-private, and the two that were exported disagreed with each other
 * about their own shape. So the one surface that has to know whether an experience is publishable —
 * the board, which shows every task at once and owns the gate — could not ask.
 *
 * ## What it is NOT
 *
 * It computes nothing. Every entry here calls the block's own `<type>BlockIssues`, which is the
 * same function the block's own editor runs, so the board can never disagree with the card beside
 * it about the same fault. A panel that derived its own verdict is exactly what the Preview
 * Problems panel did before it was deleted, and the lesson was written down: a second derivation of
 * one fact is a second thing to keep in step by hand.
 *
 * ## Why a table rather than a registry
 *
 * A `Record<BlockType, ...>` keyed by the same union `task-content` dispatches on, so a new block
 * type is a type error here rather than a silent zero. The alternative — blocks registering
 * themselves — buys indirection and loses the compiler's help with the one question that matters:
 * is every type covered?
 */
import type { BlockIssue, IssueContext } from '@/components/task/block-issues';
import type { Block, BlockType } from '@/types/experience';


import { compassBlockIssues } from '@/components/task/compass-block';
import { datavizBlockIssues } from '@/components/task/dataviz-block';
import { dropdownBlockIssues } from '@/components/task/dropdown-block';
import { fitbBlockIssues } from '@/components/task/fitb-block';
import { gpsBlockIssues } from '@/components/task/gps-block';
import { imageBlockIssues } from '@/components/task/image-block';
import { keywordBlockIssues } from '@/components/task/keyword-block';
import { mcqBlockIssues } from '@/components/task/mcq-block';
import { numericalBlockIssues } from '@/components/task/numerical-block';
import { orderingBlockIssues } from '@/components/task/ordering-block';
import { rotationBlockIssues } from '@/components/task/rotation-block';
import { scaleBlockIssues } from '@/components/task/scale-block';
import { sliderBlockIssues } from '@/components/task/slider-block';
import { sortBlockIssues } from '@/components/task/sort-block';
import { soundBlockIssues } from '@/components/task/sound-block';
import { textBlockIssues } from '@/components/task/text-block';
import { tiltBlockIssues } from '@/components/task/tilt-block';
import { variableRungIssues } from '@/lib/variable-rung-issue';


type BlockIssuesFn = (block: Block, ctx: IssueContext) => BlockIssue[];

/**
 * One entry per block type, and the compiler insists on it.
 *
 * `Record<BlockType, …>` rather than a partial map: a type added to the union and not to this table
 * fails the build, where a partial would quietly report that the new block has no problems — the
 * worst possible answer from a publication gate.
 */
const BY_TYPE: Record<BlockType, BlockIssuesFn> = {
  text: textBlockIssues,
  image: imageBlockIssues,
  sound: soundBlockIssues,
  dataviz: datavizBlockIssues,
  mcq: mcqBlockIssues,
  numerical: numericalBlockIssues,
  keyword: keywordBlockIssues,
  fitb: fitbBlockIssues,
  dropdown: dropdownBlockIssues,
  slider: sliderBlockIssues,
  gps: gpsBlockIssues,
  ordering: orderingBlockIssues,
  sort: sortBlockIssues,
  scale: scaleBlockIssues,
  compass: compassBlockIssues,
  rotation: rotationBlockIssues,
  tilt: tiltBlockIssues,
  /**
   * NOT BUILT YET — the library lists it `disabled: true`, so no task holds one.
   *
   * Spelled out rather than left to a partial map, because the compiler finding this type is the
   * whole argument for the Record: a widget that ships without an entry here would report no
   * problems and pass the gate silently. Whoever builds it changes this line.
   */
  widget: () => [],
};

/**
 * What this block would report, asked from anywhere.
 *
 * The RAW set, with no policy applied: `applyIssuePolicy` decides what a given surface voices, and
 * the two callers want different things. An editor hides incompletions while the teacher is still
 * working; the board is settled by definition, since nobody is mid-edit in a task they are not
 * looking at. Deciding that here would force one answer on both.
 */
export function issuesForBlock(block: Block, ctx: IssueContext): BlockIssue[] {
  const fn = BY_TYPE[block.type];
  if (!fn) return [];
  return [...fn(block, ctx), ...variableRungIssues(block, ctx.variableRung, ctx.variables, ctx.t)];
}

/** Every type this dispatcher answers for, for a test that wants to walk them. */
export const DISPATCHED_BLOCK_TYPES = Object.keys(BY_TYPE) as BlockType[];
